import { useState } from "react";
import { toast } from "sonner";
import { api, publicUrl, mediaUrl } from "@/lib/api";
import { ImageUp, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { StaffMembers } from "@/components/StaffMembers";
import { AffiliationPanel } from "@/components/AffiliationPanel";
import { PlanningCenter } from "@/components/PlanningCenter";
import { ThemePicker, PublicThemePicker } from "@/components/ThemePicker";

export default function Settings() {
  const { church, setChurch } = useAuth();
  const [f, setF] = useState({ name: church.name, slug: church.slug, notify_email: church.notify_email, notify_enabled: church.notify_enabled, affiliation_enabled: church.affiliation_enabled !== false });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/church", f);
      setChurch(data);
      setF((p) => ({ ...p, slug: data.slug }));
      toast.success("Settings saved");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Please check: ${d.map((x) => x.loc?.slice(-1)[0]).join(", ")}` : d || "Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/church/logo", fd);
      setChurch({ ...church, logo_file_id: data.logo_file_id });
      toast.success("Logo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const removeLogo = async () => {
    await api.delete("/church/logo");
    setChurch({ ...church, logo_file_id: null });
    toast.success("Logo removed");
  };

  return (
    <div className="max-w-2xl fade-up">
      <p className="eyebrow">Settings</p>
      <h1 className="font-display text-navy text-4xl font-semibold tracking-tight mt-2">Church settings</h1>
      <section className="card-soft p-8 md:p-10 mt-8" data-testid="branding-section">
        <p className="font-display text-navy text-lg font-semibold">Branding</p>
        <p className="text-sm text-muted-ink mt-1">Your logo appears on the assessment, results page and PDF.</p>
        <div className="flex flex-wrap items-center gap-5 mt-6">
          <div className="h-20 w-40 rounded-xl bg-paper border border-line flex items-center justify-center overflow-hidden">
            {church.logo_file_id ? <img src={mediaUrl(church.logo_file_id)} alt="Church logo" className="max-h-16 max-w-[140px] object-contain" data-testid="settings-logo-preview" />
              : <span className="text-xs text-muted-ink">No logo</span>}
          </div>
          <label className="btn-navy !py-2 cursor-pointer flex items-center gap-2" data-testid="upload-logo-btn">
            <ImageUp size={16} /> Upload logo
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="logo-file-input"
              onChange={(e) => { uploadLogo(e.target.files[0]); e.target.value = ""; }} />
          </label>
          {church.logo_file_id && <button type="button" onClick={removeLogo} className="text-sm text-red-700 flex items-center gap-1" data-testid="remove-logo-btn"><Trash2 size={14} /> Remove</button>}
        </div>
        <p className="text-xs text-muted-ink mt-3">PNG, JPG or WEBP, up to 5 MB.</p>
      </section>
      <form onSubmit={save} className="card-soft p-8 md:p-10 mt-6 space-y-7">
        <div className="space-y-2">
          <Label>Church name</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11" data-testid="settings-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Link handle</Label>
          <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} className="h-11 font-mono-dt" data-testid="settings-slug-input" />
          <p className="text-xs text-muted-ink font-mono-dt">{publicUrl(f.slug || church.slug)}</p>
          <p className="text-xs text-muted-ink">Change it anytime. Old links and QR codes keep working.</p>
        </div>
        <div className="space-y-2">
          <Label>Email results to</Label>
          <Input type="email" value={f.notify_email} onChange={(e) => setF({ ...f, notify_email: e.target.value })} className="h-11" data-testid="settings-email-input" />
        </div>
        <div className="flex items-center justify-between bg-paper rounded-xl p-5">
          <div>
            <p className="font-medium text-navy">Email me each completion</p>
            <p className="text-sm text-muted-ink">Sends DISC, top gifts and best-fit teams.</p>
          </div>
          <Switch checked={f.notify_enabled} onCheckedChange={(v) => setF({ ...f, notify_enabled: v })} data-testid="settings-notify-switch" />
        </div>
        <div className="flex items-center justify-between bg-paper rounded-xl p-5">
          <div>
            <p className="font-medium text-navy">Enable Religious Affiliation Selection</p>
            <p className="text-sm text-muted-ink">Allow your church to select a religious affiliation and use its suggested ministry library.</p>
          </div>
          <Switch checked={f.affiliation_enabled !== false} onCheckedChange={(v) => setF({ ...f, affiliation_enabled: v })} data-testid="settings-affiliation-switch" />
        </div>
        <button disabled={busy} className="btn-navy" data-testid="settings-save-btn">{busy ? "Saving..." : "Save changes"}</button>
      </form>
      <ThemePicker />
      <PublicThemePicker />
      {church.affiliation_enabled !== false && <AffiliationPanel onApplied={() => {}} />}
      {church.billing?.pro ? <><StaffMembers church={church} /><PlanningCenter church={church} /></> : (
        <div className="card-soft p-8 mt-6" data-testid="pro-upsell">
          <p className="font-display text-navy text-lg font-semibold">Unlock Pro features</p>
          <p className="text-sm text-muted-ink mt-1">Staff logins, Planning Center sync, changing your church URL, Spanish links, AI summaries & coach and PDF results are part of the Pro plan.</p>
          <a href="/billing" className="btn-navy inline-block !py-2 mt-4" data-testid="pro-upsell-btn">Upgrade to Pro</a>
        </div>)}
    </div>
  );
}
